import { useEffect, useMemo, useState } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Button,
    VStack,
    HStack,
    Flex,
    Box,
    Text,
    Input,
    InputGroup,
    InputLeftElement,
    Icon,
    Spinner,
    Tooltip,
    Badge,
    Tag,
    Center,
    Collapse,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { TbPackage } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import { useSearch, OrderReponse } from '../hooks/useSearch';
import { useGetOrderDetail, OrderedSection, SupplierOrderStatus } from '../hooks/useGetOrderDetail';
import SourceBadge from '../components/SourceBadge';
import { formatPhoneNumber } from '../functions';
import { useIsOverflowing } from '../hooks/useIsOverflowing';

const PARTBAY_BLUE = '#0071E3';

const getSupplierStatusLabel = (status: SupplierOrderStatus) => {
    switch (status) {
        case 'confirmed':
            return 'Confirmed';
        case 'ordered':
            return 'Ordered';
        case 'rejected':
            return 'Rejected';
        case 'cancelled':
            return 'Cancelled';
        default:
            return status;
    }
};

const getStatusColor = (status: SupplierOrderStatus) => {
    switch (status) {
        case 'confirmed':
            return 'purple';
        case 'ordered':
            return 'teal';
        case 'rejected':
            return 'gray';
        case 'cancelled':
            return 'red';
        default:
            return 'gray';
    }
};

export interface NewChatTarget {
    mode: 'order' | 'supplier';
    orderId: string;
    supplierKey?: string;
}

interface NewChatModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** When provided (e.g. inside the /chat tab), resolve/open the thread in-page instead of navigating. */
    onStartChat?: (target: NewChatTarget) => void;
}

type ChatType = 'internal' | 'vendor';

const NewChatModal = ({ isOpen, onClose, onStartChat }: NewChatModalProps) => {
    const navigate = useNavigate();

    const [searchString, setSearchString] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selectedOrder, setSelectedOrder] = useState<OrderReponse | null>(null);
    const [chatType, setChatType] = useState<ChatType>('internal');
    const [selectedSupplierKey, setSelectedSupplierKey] = useState('');

    // Reset all state whenever the modal is opened/closed.
    useEffect(() => {
        setSearchString('');
        setDebouncedSearch('');
        setSelectedOrder(null);
        setChatType('internal');
        setSelectedSupplierKey('');
    }, [isOpen]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchString), 150);
        return () => clearTimeout(timer);
    }, [searchString]);

    const { data: searchData, isLoading: isSearching } = useSearch(
        debouncedSearch,
        debouncedSearch.trim().length >= 2
    );

    const orderResults = useMemo(
        () => (searchData?.results ?? []).filter((r): r is OrderReponse => r.type === 'order'),
        [searchData?.results]
    );

    const { data: orderDetail, isLoading: isOrderDetailLoading } = useGetOrderDetail(
        selectedOrder?.orderId ?? '',
        { enabled: !!selectedOrder }
    );

    // Supplier orders are spread across the ordered/confirmed/etc. tabs (the 'all'
    // tab is not a reliable superset), so flatten every tab's sections and dedupe by
    // key. Mirrors how OrderDetail builds its Supplier Communications list, so the
    // vendor toggle matches exactly which vendors you can message on the order page.
    const supplierSections = useMemo(() => {
        const seen = new Set<string>();
        const sections: OrderedSection[] = [];
        for (const tab of orderDetail?.ordered ?? []) {
            for (const section of tab.sections) {
                if (section.source !== 'partstrader' && section.source !== 'opstrax' && section.source !== 'ccc') continue;
                if (seen.has(section.key)) continue;
                seen.add(section.key);
                sections.push(section);
            }
        }
        return sections;
    }, [orderDetail?.ordered]);
    const hasSuppliers = supplierSections.length > 0;
    const vendorDisabled = !selectedOrder || isOrderDetailLoading || !hasSuppliers;
    const [bodyRef, bodyOverflows] = useIsOverflowing(isOpen);

    // If suppliers disappear (or order changes), fall back to internal.
    useEffect(() => {
        if (chatType === 'vendor' && vendorDisabled) {
            setChatType('internal');
            setSelectedSupplierKey('');
        }
    }, [chatType, vendorDisabled]);

    const handleSelectOrder = (order: OrderReponse) => {
        setSelectedOrder(order);
        setChatType('internal');
        setSelectedSupplierKey('');
    };

    const handleChangeOrder = () => {
        setSelectedOrder(null);
        setChatType('internal');
        setSelectedSupplierKey('');
    };

    const canSubmit =
        !!selectedOrder && (chatType === 'internal' || (chatType === 'vendor' && !!selectedSupplierKey));

    const handleSubmit = () => {
        if (!selectedOrder || !canSubmit) return;
        const target = {
            mode: (chatType === 'vendor' ? 'supplier' : 'order') as 'order' | 'supplier',
            orderId: selectedOrder.orderId,
            supplierKey: chatType === 'vendor' ? selectedSupplierKey : undefined,
        };
        if (onStartChat) {
            onStartChat(target);
        } else {
            navigate('/chat', { state: { newChat: target } });
        }
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader
                    fontSize="lg"
                    fontWeight="bold"
                    color="gray.800"
                    bg="gray.50"
                    borderBottomWidth={bodyOverflows ? '1px' : '0'}
                    borderBottomStyle="solid"
                    borderColor="gray.300"
                    borderTopRadius="md"
                >
                    New Chat
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody ref={bodyRef} minH={0} pt={4}>
                    <VStack align="stretch" spacing={5}>
                        {/* Step 1 — Work file search / selected summary */}
                        {selectedOrder ? (
                            <Box
                                borderWidth="1px"
                                borderColor="gray.200"
                                borderRadius="md"
                                p={3}
                                bg="gray.50"
                            >
                                <HStack justify="space-between" align="start">
                                    <VStack align="start" spacing={1}>
                                        <HStack spacing={2}>
                                            <Icon
                                                as={TbPackage as React.ElementType}
                                                w="16px"
                                                h="16px"
                                                color="gray.700"
                                            />
                                            <Text fontWeight="semibold" fontSize="sm" color="gray.800">
                                                RO {selectedOrder.roNumber || '—'}
                                            </Text>
                                            {selectedOrder.archived && (
                                                <Badge
                                                    bg="orange.100"
                                                    color="orange.700"
                                                    fontSize="xs"
                                                    borderRadius="full"
                                                >
                                                    Archived
                                                </Badge>
                                            )}
                                        </HStack>
                                        <Text fontSize="xs" color="gray.500">
                                            {selectedOrder.vehicle}
                                        </Text>
                                        <Text fontSize="xs" color="gray.500">
                                            {selectedOrder.location}
                                            {selectedOrder.claimNumber ? ` • Claim #${selectedOrder.claimNumber}` : ''}
                                        </Text>
                                    </VStack>
                                    <Button
                                        size="xs"
                                        variant="link"
                                        color={PARTBAY_BLUE}
                                        onClick={handleChangeOrder}
                                    >
                                        Change
                                    </Button>
                                </HStack>
                            </Box>
                        ) : (
                            <VStack align="stretch" spacing={2}>
                                <InputGroup size="md">
                                    <InputLeftElement pointerEvents="none">
                                        <SearchIcon color="gray.400" boxSize={3.5} />
                                    </InputLeftElement>
                                    <Input
                                        autoFocus
                                        placeholder="Search by VIN, RO, claim #, customer, or vehicle..."
                                        value={searchString}
                                        onChange={(e) => setSearchString(e.target.value)}
                                        borderColor="gray.300"
                                        _hover={{ borderColor: 'gray.400' }}
                                        _focus={{ borderColor: PARTBAY_BLUE, boxShadow: 'none' }}
                                    />
                                </InputGroup>

                                {debouncedSearch.trim().length >= 2 && (
                                    <Box
                                        borderWidth="1px"
                                        borderColor="gray.200"
                                        borderRadius="md"
                                        maxH="260px"
                                        overflowY="auto"
                                    >
                                        {isSearching ? (
                                            <Center py={6}>
                                                <Spinner size="sm" color={PARTBAY_BLUE} />
                                            </Center>
                                        ) : orderResults.length === 0 ? (
                                            <Center py={6}>
                                                <Text fontSize="sm" color="gray.500">
                                                    No work files found
                                                </Text>
                                            </Center>
                                        ) : (
                                            <VStack align="stretch" spacing={0}>
                                                {orderResults.map((order) => (
                                                    <Box
                                                        key={order.orderId}
                                                        as="button"
                                                        type="button"
                                                        textAlign="left"
                                                        px={3}
                                                        py={2.5}
                                                        borderBottomWidth="1px"
                                                        borderColor="gray.100"
                                                        _hover={{ bg: 'gray.50' }}
                                                        onClick={() => handleSelectOrder(order)}
                                                    >
                                                        <HStack spacing={2} mb={0.5}>
                                                            <Text fontWeight="medium" fontSize="sm" color="gray.800">
                                                                RO {order.roNumber || '—'}
                                                            </Text>
                                                            {order.archived && (
                                                                <Badge
                                                                    bg="orange.100"
                                                                    color="orange.700"
                                                                    fontSize="xs"
                                                                    borderRadius="full"
                                                                >
                                                                    Archived
                                                                </Badge>
                                                            )}
                                                        </HStack>
                                                        <Text fontSize="xs" color="gray.500">
                                                            {order.vehicle}
                                                        </Text>
                                                        <Text fontSize="xs" color="gray.500">
                                                            {order.location}
                                                            {order.customer ? ` • ${order.customer}` : ''}
                                                        </Text>
                                                    </Box>
                                                ))}
                                            </VStack>
                                        )}
                                    </Box>
                                )}
                            </VStack>
                        )}

                        {/* Step 2 — Internal vs. Vendor */}
                        {selectedOrder && (
                            <VStack align="stretch" spacing={3}>
                                <Text fontSize="sm" fontWeight="medium" color="gray.700">
                                    Chat type
                                </Text>
                                <HStack spacing={2}>
                                    <Button
                                        flex={1}
                                        size="sm"
                                        variant="outline"
                                        bg={chatType === 'internal' ? PARTBAY_BLUE : 'transparent'}
                                        color={chatType === 'internal' ? 'white' : 'gray.600'}
                                        borderColor={chatType === 'internal' ? PARTBAY_BLUE : 'gray.300'}
                                        _hover={chatType === 'internal' ? { bg: PARTBAY_BLUE } : { bg: 'gray.50' }}
                                        _active={chatType === 'internal' ? { bg: PARTBAY_BLUE } : { bg: 'gray.50' }}
                                        onClick={() => {
                                            setChatType('internal');
                                            setSelectedSupplierKey('');
                                        }}
                                    >
                                        Internal
                                    </Button>
                                    <Tooltip
                                        label="No parts have been ordered yet."
                                        placement="top"
                                        isDisabled={!vendorDisabled || isOrderDetailLoading}
                                        hasArrow
                                    >
                                        <Box flex={1}>
                                            <Button
                                                w="100%"
                                                size="sm"
                                                variant="outline"
                                                isDisabled={vendorDisabled}
                                                bg={chatType === 'vendor' ? PARTBAY_BLUE : 'transparent'}
                                                color={chatType === 'vendor' ? 'white' : 'gray.600'}
                                                borderColor={chatType === 'vendor' ? PARTBAY_BLUE : 'gray.300'}
                                                _hover={chatType === 'vendor' ? { bg: PARTBAY_BLUE } : { bg: 'gray.50' }}
                                                _active={chatType === 'vendor' ? { bg: PARTBAY_BLUE } : { bg: 'gray.50' }}
                                                onClick={() => setChatType('vendor')}
                                            >
                                                Vendor
                                            </Button>
                                        </Box>
                                    </Tooltip>
                                </HStack>

                                <Collapse in={chatType === 'vendor'} animateOpacity>
                                    <VStack align="stretch" spacing={2} pt={1}>
                                        {supplierSections.map((section) => {
                                            const isSelected = selectedSupplierKey === section.key;
                                            const messageCount = orderDetail?.supplierMessageCounts?.[section.key] ?? 0;
                                            return (
                                                <Box
                                                    key={section.key}
                                                    as="button"
                                                    type="button"
                                                    textAlign="left"
                                                    position="relative"
                                                    p={3}
                                                    borderRadius="md"
                                                    border="1px solid"
                                                    borderColor={isSelected ? PARTBAY_BLUE : 'gray.200'}
                                                    bg={isSelected ? '#0071E30a' : 'white'}
                                                    boxShadow={isSelected ? 'sm' : 'none'}
                                                    _hover={{ borderColor: PARTBAY_BLUE, boxShadow: 'none' }}
                                                    transition="border-color 120ms ease, box-shadow 120ms ease"
                                                    onClick={() => setSelectedSupplierKey(section.key)}
                                                >
                                                    <HStack justify="space-between" align="flex-start" spacing={2}>
                                                        <VStack align="flex-start" spacing={1} minW={0} flex={1}>
                                                            <HStack spacing={2} minW={0} w="100%">
                                                                <SourceBadge source={section.source ?? 'partstrader'} />
                                                                <Tooltip label={section.title} placement="top" openDelay={300}>
                                                                    <Text fontSize="md" fontWeight="semibold" color="gray.800" noOfLines={1} minW={0}>
                                                                        {section.title}
                                                                    </Text>
                                                                </Tooltip>
                                                                {section.displayId && (
                                                                    <Text
                                                                        fontSize="xs"
                                                                        color="gray.500"
                                                                        fontWeight="medium"
                                                                        bg="gray.50"
                                                                        border="1px solid"
                                                                        borderColor="gray.200"
                                                                        borderRadius="md"
                                                                        px={1.5}
                                                                        py={0.5}
                                                                        lineHeight="1.2"
                                                                        flexShrink={0}
                                                                        noOfLines={1}
                                                                    >
                                                                        {section.displayId}
                                                                    </Text>
                                                                )}
                                                            </HStack>
                                                            {(section.phoneNumber || section.email) && (
                                                                <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                                                    {section.phoneNumber ? formatPhoneNumber(section.phoneNumber) : ''}
                                                                    {section.phoneNumber && section.email ? ' • ' : ''}
                                                                    {section.email ?? ''}
                                                                </Text>
                                                            )}
                                                        </VStack>
                                                        <Tag
                                                            size="sm"
                                                            colorScheme={getStatusColor(section.status)}
                                                            variant="subtle"
                                                            px={1.5}
                                                            fontSize="11px"
                                                            flexShrink={0}
                                                        >
                                                            {getSupplierStatusLabel(section.status)}
                                                        </Tag>
                                                    </HStack>
                                                    {messageCount > 0 && (
                                                        <Flex
                                                            position="absolute"
                                                            bottom="10px"
                                                            right="10px"
                                                            minW="18px"
                                                            h="18px"
                                                            px="5px"
                                                            borderRadius="full"
                                                            bg={PARTBAY_BLUE}
                                                            color="white"
                                                            align="center"
                                                            justify="center"
                                                            fontSize="10px"
                                                            fontWeight="bold"
                                                            lineHeight="1"
                                                        >
                                                            {messageCount}
                                                        </Flex>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </VStack>
                                </Collapse>
                            </VStack>
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter justifyContent="space-between" pb={4}>
                    <Button size="action" variant="outlineGray" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        size="action"
                        bg={PARTBAY_BLUE}
                        color="white"
                        _hover={{ bg: '#005BC4' }}
                        isDisabled={!canSubmit}
                        onClick={handleSubmit}
                    >
                        Start Chat
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default NewChatModal;
